import { useTranslation } from "react-i18next"

import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { cn } from "@/shared/lib/utils"
import { handleNumericKeyDown, sanitizeNumericInput } from "@/shared/lib/input-helpers"

import { calculateDistributionTotal } from "../utils/form"
import { DAY_DISTRIBUTION_FIELDS } from "../constants/form"
import type { WeeklyCalculatorFormValues, WeeklyCalculatorFormErrors } from "../types/form"

interface DayDistributionInputProps {
  values: WeeklyCalculatorFormValues
  errors: WeeklyCalculatorFormErrors
  onChange: (field: keyof WeeklyCalculatorFormValues, value: string) => void
}

export function DayDistributionInput({ values, errors, onChange }: DayDistributionInputProps) {
  const { t } = useTranslation()

  const total = calculateDistributionTotal(values)
  const isValid = Math.abs(total - 100) < 0.01

  const totalColorClassName = isValid
    ? "text-[var(--color-text-primary)]"
    : "text-[var(--color-error)]"

  return (
    <div className="contents">
      {/* Label on the left */}
      <div className="flex items-start gap-2 pt-0 sm:pt-2">
        <Label>{t("form.fields.distribution.label")}</Label>
      </div>

      {/* All day inputs stacked on the right */}
      <div className="form-input-container stack-sm">
        <div className="space-y-3">
          {DAY_DISTRIBUTION_FIELDS.map((field) => {
            const error = Boolean(errors[field.name])
            const value = values[field.name]
            const dayLabel = t(field.labelKey)

            return (
              <div key={field.name} className="flex items-center gap-2">
                <Input
                  aria-invalid={error}
                  aria-describedby={error ? `${field.name}-error` : undefined}
                  aria-label={dayLabel}
                  autoComplete="off"
                  className={cn(
                    "!w-24 max-w-24",
                    error &&
                      "border-destructive focus:ring-destructive focus-visible:ring-destructive",
                  )}
                  id={field.name}
                  inputMode="decimal"
                  name={field.name}
                  placeholder="0"
                  type="text"
                  value={value}
                  onKeyDown={handleNumericKeyDown}
                  onChange={(event) =>
                    onChange(field.name, sanitizeNumericInput(event.target.value))
                  }
                />
                <span className="body-sm whitespace-nowrap text-[var(--color-text-secondary)]">
                  % {dayLabel}
                </span>
                {error ? (
                  <p className="field-error text-sm" id={`${field.name}-error`}>
                    {errors[field.name] ? t(errors[field.name]!) : null}
                  </p>
                ) : null}
              </div>
            )
          })}

          {/* Total row */}
          <div className="flex items-center gap-2">
            <Input
              aria-label={t("form.fields.distribution.total")}
              className={cn(
                "!w-24 max-w-24 disabled:opacity-100",
                isValid
                  ? "disabled:border-[var(--color-text-primary)] disabled:text-[var(--color-text-primary)]"
                  : "disabled:border-[var(--color-error-border)] disabled:text-[var(--color-error)]",
              )}
              disabled
              readOnly
              type="text"
              value={total.toFixed(1)}
            />
            <span className={cn("body-sm whitespace-nowrap", totalColorClassName)}>
              % {t("form.fields.distribution.total")}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
